import React, { useEffect, useState } from "react";
import LmsTable from "../ui/LmsTable";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import LmsTabPages from "../ui/LmsTabPages";
import BCModal from "../../ui/BCModal";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { WidthType } from "../../interfaces/Global";
import { guid } from "../../../Helpers";

interface BudgetTemplate {
  id: string;
  name: string;
}

enum PhasesToModifyKeys {
  PhaseOne,
  PhaseTwo,
}

interface Budget {
  id: string;
  amount: string;
  isMandatory: boolean;
  loanBudgetId: string;
  loanBudgetCategoryId: string;
  loanBudgetSubCategory: string | null;
  monthOfRemainingExpenditure: number | null;
  disbursed?: number;
  remaining?: number;
}

const setPropertyDetailsDisplayFields = (form) => {
  form.addGroupFields([
    form.getDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    form.getDisplayField("Special Purpose Entity", { name: "specialPurposeEntityName" }),
    form.getDisplayField("Loan", { name: "displayWithLoanNumber" }),
    form.getDisplayField("Property", { name: "display" }),
  ]);
};

const selectPhaseToModifyFields = (form) => {
  setPropertyDetailsDisplayFields(form);

  form.addGroupFields([
    form.getCurrencyField("Phase 1 Budget", { name: "budgetAmountTotal", disabled: true }),
    form.getCurrencyField("Phase 1 Disbursements", { name: "disbursedTotal", disabled: true }),
    form.getCurrencyField("Available for Phase 2", { name: "availableTotal", disabled: true }),
  ]);

  form.addGroupFields(
    [
      form.getBlankField({ width: 2 }),
      form.getGroupRadioField("Modify Phase 1 Budget", {
        value: PhasesToModifyKeys.PhaseOne,
      }),
      form.getBlankField({ width: 2 }),
      form.getGroupRadioField("Select new template for Phase 2 Budget", {
        value: PhasesToModifyKeys.PhaseTwo,
      }),
      form.getBlankField({ width: 2 }),
    ],
    {
      radioGroupName: "phaseToModifySelected",
    }
  );

  form.addGroupFields([
    form.getBlankField({ width: 2 }),
    form.getDisplayField("Modify Phase 1 Budget Explanation", {
      component: (
        <i>
          If you modify your Phase 1 Budget you will be required to manually adjust budget totals to
          ensure you do not exceed the amount available for Phase 2
        </i>
      ),
    }),
    form.getBlankField({ width: 2 }),
    form.getDisplayField("Select new template for Phase 2 Budget Explanation", {
      component: (
        <i>
          If you select a new template, you will not see Phase 1 budget items. You will not have to
          adjust Phase 1 budget totals. Your budget is limited to the amount in Available for Phase
          2 budget
        </i>
      ),
    }),
    form.getBlankField({ width: 2 }),
  ]);

  return form;
};

const AddBudgetForm = ({ onClose, onSubmit }) => {
  const form = new BCFormController();
  [
    [
      form.getDropdownField("Category", "/metadata/list-loan-budget-categories", {
        required: true,
        name: "loanBudgetCategoryId",
      }),
    ],
    [
      form.getInputField("Sub-Category", { name: "loanBudgetSubCategory" }),
      form.getCheckboxField("Mandatory", { alignRight: true, name: "isMandatory" }),
    ],
    [
      form.getCurrencyField("Budget Amount", {
        required: true,
        name: "amount",
      }),
    ],
    [form.getNumberField("Remaining Expenditure Month", { name: "monthOfRemainingExpenditure" })],
  ].forEach((group) => form.addCenteredGroup(group));

  return (
    <LmsFormGenerator
      myForm={form}
      handleClose={onClose}
      handleSubmit={(inputs) => {
        onSubmit(inputs);
        onClose();
      }}
      actions={[{ action: "save", text: "Add" }, "close"]}
    />
  );
};

const modifyPhaseOneBudgetFields = (
  form: BCFormController,
  listLoanBudgetCategories,
  budgets: Budget[],
  handleRemove,
  handleAdd
) => {
  setPropertyDetailsDisplayFields(form);
  form.addGroupFields([
    form.getButtonField("", "", {
      buttonIcon: GlobalConstants.Icons.ADD,
      width: 1,
      modalContent: (setIsAddFormModalOpen) => (
        <AddBudgetForm onSubmit={handleAdd} onClose={() => setIsAddFormModalOpen(false)} />
      ),
    }),
    ...(
      [
        ["Category", 2],
        ["Sub-category", 3],
        ["Mandatory", 1],
        ["Budget Amount", 2],
        ["Disbursed", 2],
        ["Available", 2],
        ["Remaining Expenditure Month", 2],
        ["Payee", 1],
      ] as [string, WidthType][]
    ).map(([label, width]) => form.getLabelField(label, { position: "center", width })),
  ]);

  if (listLoanBudgetCategories.length > 0 && budgets.length > 0) {
    budgets.forEach(
      ({
        id,
        amount,
        disbursed,
        remaining,
        isMandatory,
        loanBudgetCategoryId,
        loanBudgetSubCategory,
        monthOfRemainingExpenditure,
      }) => {
        form.addGroupFields([
          form.getButtonField("", handleRemove.bind(null, id), {
            buttonIcon: GlobalConstants.Icons.CLOSE,
            width: 1,
          }),
          form.getDropdownField("Category", "/metadata/list-loan-budget-categories", {
            label: "",
            width: 2,
            disabled: true,
            nestedInputsProp: id,
            value: loanBudgetCategoryId,
          }),
          form.getLabelField(loanBudgetSubCategory || "", {
            width: 3,
            position: "center",
            wrapLabel: true,
            fontWeightLight: true,
          }),
          form.getLabelField(isMandatory ? "True" : "False", {
            width: 1,
            wrapLabel: true,
            noBackground: true,
            fontWeightLight: true,
          }),
          form.getCurrencyField("Budget Amount", {
            label: "",
            width: 2,
            value: amount,
            name: "amount",
            calculatedTargetField: "totalBudgetAmount",
            nestedInputsProp: id,
          }),
          form.getCurrencyField("Disbursed", {
            label: "",
            width: 2,
            value: disbursed || 0,
            nestedInputsProp: id,
            calculatedTargetField: "totalDisbursed",
            disabled: true,
            noBackground: true,
          }),
          form.getCurrencyField("Available", {
            label: "",
            value: remaining || 0,
            width: 2,
            disabled: true,
            noBackground: true,
            nestedInputsProp: id,
            calculatedTargetField: "totalRemaining",
          }),
          form.getLabelField(
            monthOfRemainingExpenditure !== null ? monthOfRemainingExpenditure + "" : "",
            {
              width: 2,
              position: "center",
            }
          ),
          form.getDisplayField("Payee", {
            width: 1,
            label: "",
            noBackground: true,
          }),
        ]);
      }
    );

    form.addGroupFields([
      form.getLabelField("Totals", { width: 7, position: "right-center" }),
      ...["totalBudgetAmount", "totalDisbursed", "totalRemaining"].map((totalField) =>
        form.getCurrencyField(totalField, {
          value: 0,
          width: 2,
          label: "",
          disabled: true,
          noBackground: true,
        })
      ),
    ]);
  }

  return form;
};

const SelectNewTemplateForPhaseTwoBudget = ({ property }) => {
  const [templateIdSubmitted, setTemplateIdSubmitted] = useState<string | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<BudgetTemplate | null>(null);
  const [templateModalId, setTemplateIdModal] = useState(null);
  const [{ data: selectPhaseData = {}, isLoading: isSelectPhaseLoading, error: selectPhaseError }] =
    useFetchData(`/budget/select-phase2/${property.id}`, {
      method: "POST",
    });

  const [{ isLoading: isSelectTemplateLoading, error: selectTemplateError }] = useFetchData(
    currentTemplate
      ? `/budget/select-template/${selectPhaseData.loanBudgetTempId}/${currentTemplate.id}`
      : "",
    { method: "POST" }
  );

  const form = new BCFormController(
    {
      ...property.data,
      ...selectPhaseData,
    },
    {
      submitMethod: "POST",
      submitSuffix: `/budget/submit-template/${selectPhaseData.loanBudgetTempId}`,
    }
  );

  const handleSelectTemplate = ({ firstCheckedRow: template, setCheckedItems }) => {
    setCheckedItems([]);
    setCurrentTemplate(template);
  };

  const handleClickTemplate = ({ clickedRow: template }) => {
    setTemplateIdModal(template.id);
  };

  form.addGroupFields([
    form.getDisplayField("Borrower", { name: "borrowerOrganizationName" }),
    form.getDisplayField("Application #", { name: "applicationNumberDisplay" }),
    form.getDisplayField("Special Purpose Entity", { name: "specialPurposeEntityName" }),
    form.getDisplayField("Selected Property", { name: "display" }),
  ]);

  form.addCenteredGroup([
    form.getDisplayField("Selected Template", {
      value: currentTemplate ? currentTemplate.name : "",
    }),
  ]);

  form.addComponent(
    <LmsTabPages
      tabPages={{
        "Budget Templates": (
          <LmsTable
            apiSuffix="/budget/templates"
            handleClick={handleClickTemplate}
            actionButtons={[
              "export",
              {
                text: "Select Template",
                icon: GlobalConstants.Icons.ADD,
                handleClick: handleSelectTemplate,
                isDisabled: (checkedItems) => checkedItems.length !== 1,
              },
            ]}
            columnClickOptions={{
              name: {
                path: "",
                idProp: "id",
              },
            }}
          />
        ),
      }}
    />
  );

  return (
    <>
      <BCModal
        closeIcon
        isModalOpen={templateModalId !== null}
        onClose={() => setTemplateIdModal(null)}
        content={
          <LmsTabPages
            tabPages={{
              Details: (
                <LmsTable
                  actionButtons={["export"]}
                  apiSuffix={`budget/templates/${templateModalId}/details`}
                />
              ),
            }}
          />
        }
      />
      <LmsFormGenerator
        myForm={form}
        error={selectPhaseError || selectTemplateError}
        isLoading={isSelectPhaseLoading || isSelectTemplateLoading}
        onSubmitSuccess={() => setTemplateIdSubmitted(currentTemplate ? currentTemplate.id : null)}
        actions={[
          "close",
          ...(!(currentTemplate === null || templateIdSubmitted === currentTemplate.id)
            ? [{ action: "save", text: "Submit" }]
            : []),
        ]}
      />
    </>
  );
};

const ModifyPhaseOneBudget = ({ property }) => {
  const budgetUrl = `/budget/details/${property.id}`;
  const [budget, setBudget] = useState<Budget[]>([]);
  const [{ data: listLoanBudgetCategories = [], isLoading: isListLoading, error: listError }] =
    useFetchData("/metadata/list-loan-budget-categories");

  const [{ data: budgetData = [], isLoading: isBudgetLoading, error: budgetError }] =
    useFetchData(budgetUrl);

  const dataFormatterFunc = () => {
    return budget.map(
      ({
        id,
        amount,
        isMandatory,
        loanBudgetId,
        loanBudgetCategoryId,
        loanBudgetSubCategory,
        monthOfRemainingExpenditure,
      }) => ({
        id,
        loanBudgetId,
        loanBudgetCategoryId,
        loanBudgetSubCategory,
        monthOfRemainingExpenditure,
        isMandatory: !!isMandatory,
        loanPropertyId: property.id,
        budgetAmount: parseFloat(amount),
      })
    );
  };

  const handleAdd = ({
    amount,
    loanBudgetCategoryId,
    isMandatory = false,
    loanBudgetSubCategory = null,
    monthOfRemainingExpenditure = null,
  }) => {
    setBudget((prevBudget) => [
      ...prevBudget,
      {
        amount,
        id: guid(),
        isMandatory,
        loanBudgetCategoryId,
        loanBudgetSubCategory,
        monthOfRemainingExpenditure,
        loanBudgetId: property.data.loanBudgetId,
      },
    ]);
  };

  const handleRemove = (id) => {
    setBudget((prevBudget) => prevBudget.filter(({ id: budgetId }) => id !== budgetId));
  };

  const myForm = modifyPhaseOneBudgetFields(
    new BCFormController(
      { ...property.data },
      {
        isCompact: true,
        dataFormatterFunc,
        submitMethod: "POST",
        submitSuffix: `/budget/mark-budget-complete/${property.id}`,
        clearSuffixesOnSubmit: [budgetUrl],
      }
    ),
    listLoanBudgetCategories,
    budget,
    handleRemove,
    handleAdd
  );

  useEffect(() => {
    setBudget(budgetData);
  }, [budgetData]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      error={listError || budgetError}
      isLoading={isListLoading || isBudgetLoading}
      actions={[{ action: "save", text: "Mark Budget Complete" }, "close"]}
    />
  );
};

const SelectPhaseToModify = ({ handleSelectPhaseToModify, property }) => {
  const budgetEndpoint = `/loanproperty/loan-budget-info/${property.id}`;
  const [{ data: loanBudgetInfo = {}, isLoading: isLoanBudgetLoading, error: loanBudgetError }] =
    useFetchData(budgetEndpoint);

  const myForm = selectPhaseToModifyFields(
    new BCFormController(
      { ...loanBudgetInfo, ...property.data },
      { clearSuffixesOnSubmit: [budgetEndpoint] }
    )
  );

  const handleNext = ({ phaseToModifySelected }) => {
    handleSelectPhaseToModify(phaseToModifySelected);
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      error={loanBudgetError || property.error}
      isLoading={isLoanBudgetLoading || property.isLoading}
      actions={[
        "close",
        {
          text: "Next",
          handleClick: handleNext,
        },
      ]}
    />
  );
};

const LmsServicingModifyBTBBudgetsEditForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`/loanproperty/${id}`);

  const [phaseToModifySelected, setPhaseToModifySelected] = useState<PhasesToModifyKeys | null>(
    null
  );

  const property = {
    id,
    data,
    error,
    isLoading,
  };

  const handleSelectPhaseToModify = (phase) => {
    setPhaseToModifySelected(phase);
  };

  switch (phaseToModifySelected) {
    case PhasesToModifyKeys.PhaseOne:
      return <ModifyPhaseOneBudget property={property} />;
    case PhasesToModifyKeys.PhaseTwo:
      return <SelectNewTemplateForPhaseTwoBudget property={property} />;
    default:
      return (
        <SelectPhaseToModify
          property={property}
          handleSelectPhaseToModify={handleSelectPhaseToModify}
        />
      );
  }
};

const ModifyBTBBudgetsTable = () => (
  <LmsTable
    apiSuffix="/budget/phase2-properties"
    actionButtons={["edit", "export"]}
    columnClickOptions={{
      loan: {
        path: "/servicing-modify-btb-budgets/edit/{id}",
        idProp: "id",
      },
    }}
    columnLabelsDictionary={{
      applicationNumber: "Application #",
      borrowerOrganization: "Borrower",
    }}
  />
);

const LmsServicingModifyBTBBudgets = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(params, ["edit"]);

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <ModifyBTBBudgetsTable />}
      {isEdit && <LmsServicingModifyBTBBudgetsEditForm id={id} />}
    </>
  );
};

export default LmsServicingModifyBTBBudgets;
